import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum } from "@/lib/finance";
import { fmtUSD, fmtPct } from "@/lib/format";

export default function InvestmentRoiPage() {
    const [initial, setInitial] = useState(10000);
    const [final, setFinal] = useState(14500);
    const [years, setYears] = useState(3);

    const result = useMemo(() => {
        const i = toNum(initial);
        const f = toNum(final);
        const y = Math.max(0.01, toNum(years));
        const totalReturn = i > 0 ? (f - i) / i : 0;
        const cagr = i > 0 && f > 0 ? Math.pow(f / i, 1 / y) - 1 : 0;
        const profit = f - i;
        return { totalReturn, cagr, profit };
    }, [initial, final, years]);

    return (
        <CalculatorShell
            slug="investment-roi"
            explainer={
                                <>
                    <h2>How this investment ROI calculator works</h2>
                    <p>
                        An investment ROI calculator measures your return on investment as a percentage in
                        simple or annualized terms. Use it to evaluate past performance, compare two
                        opportunities on equal footing, or calculate what a past investment actually returned.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Simple ROI:</strong> (Ending Value − Beginning Value) ÷ Beginning Value × 100 — straightforward but doesn't account for time.</li>
                        <li><strong>CAGR (annualized ROI):</strong> (Ending ÷ Beginning)^(1/years) − 1 — the compound annual growth rate, ideal for comparing investments held different lengths of time.</li>
                        <li><strong>Net gain in dollars:</strong> shown alongside percentage so you can weigh both dimensions.</li>
                        <li><strong>Fees &amp; taxes in cost basis:</strong> including all costs in your initial investment figure gives an accurate real return, not just a nominal one.</li>
                        <li><strong>Negative ROI:</strong> handled the same way mathematically — a loss is simply a negative percentage.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Always use annualized ROI (CAGR) when comparing investments held for different time periods.</li>
                        <li>Include all costs — commissions, fees, taxes — in your initial investment figure for an honest number.</li>
                        <li>Don't confuse ROI with IRR; IRR accounts for the timing of cash flows and is more accurate for ongoing investments.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Initial investment" prefix="$" value={initial} onChange={setInitial} dataTestId="calc-input-initial" />
                    <Field label="Final value" prefix="$" value={final} onChange={setFinal} dataTestId="calc-input-final" />
                    <Field label="Years held" value={years} onChange={setYears} step={0.25} dataTestId="calc-input-years" />
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Annualized return (CAGR)"
                        value={result.cagr * 100}
                        format={(n) => fmtPct(n, 2)}
                        secondary={[
                            { label: "Total return", value: result.totalReturn * 100, format: (n) => fmtPct(n, 1), testId: "calc-result-total" },
                            { label: "Net profit", value: result.profit, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-profit" },
                        ]}
                    />
                </div>
            </div>
        </CalculatorShell>
    );
}
